/** @format — ChatGPT-style floating composer with live voice states */

import React, { useEffect, useState, type KeyboardEvent, type RefObject } from "react";
import { CircularProgress } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { cn } from "@/lib/utils";
import { CheckIcon, CloseIcon, LoadingSunIcon, MicrophoneIcon, WaveformBarsIcon } from "./chatInputIcons";
import { VoiceNoteActivityDots, VoiceNoteWaveform } from "./VoiceWaveDecorations";

const DISCLAIMER = "Dockie may be inaccurate. Please check important info.";

export type VoiceSessionStatus =
  | "idle"
  | "requesting-mic"
  | "connecting"
  | "waiting-ready"
  | "listening"
  | "thinking"
  | "speaking"
  | "transcribing"
  | "stopping";

export type VoiceInputMode = "chat" | "note" | null;

type VoiceNotePhase = "connecting" | "listening" | "transcribing" | "recorded" | "error";
type VoiceChatPhase = "connecting" | "listening" | "thinking" | "speaking" | "stopping";
type SelectedChatFile = {
  name: string;
  type?: string;
  size?: number;
  preview?: string | null;
};

export interface FloatingChatInputProps {
  input: string;
  setInput: (value: string) => void;
  onSend: () => void;
  onUploadClick?: () => void;
  loading?: boolean;
  inputRef?: RefObject<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleKeyPress?: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  selectedFile?: SelectedChatFile | null;
  onRemoveFile?: () => void;
  multiline?: boolean;
  showUpload?: boolean;
  showDisclaimer?: boolean;
  showVoice?: boolean;
  className?: string;
  placeholder?: string;
  voiceStatus?: VoiceSessionStatus;
  voiceMode?: VoiceInputMode;
  userTranscript?: string;
  assistantTranscript?: string;
  voiceNoteRecorded?: boolean;
  voiceNoteDraft?: string;
  voiceSessionError?: string | null;
  onClearVoiceSessionError?: () => void;
  onVoiceNoteEdited?: () => void;
  onStartVoiceChat?: () => void;
  onStartVoiceNote?: () => void;
  onStopVoiceChat?: () => void;
  onCancelVoice?: () => void;
  onConfirmVoiceNote?: () => void;
  voiceMediaStream?: MediaStream | null;
}

function PlusIcon({ className = "" }: { className?: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 16 16" fill="none" className={className} aria-hidden>
      <path
        d="M12.6667 7.50004C12.9428 7.50004 13.1667 7.7239 13.1667 8.00004C13.1667 8.27618 12.9428 8.50004 12.6667 8.50004H3.33337C3.05723 8.50004 2.83337 8.27618 2.83337 8.00004C2.83337 7.7239 3.05723 7.50004 3.33337 7.50004H12.6667Z"
        fill="currentColor"
      />
      <path
        d="M7.50004 12.6667V3.33337C7.50004 3.05723 7.7239 2.83337 8.00004 2.83337C8.27618 2.83337 8.50004 3.05723 8.50004 3.33337V12.6667C8.50004 12.9428 8.27618 13.1667 8.00004 13.1667C7.7239 13.1667 7.50004 12.9428 7.50004 12.6667Z"
        fill="currentColor"
      />
    </svg>
  );
}

function CancelDotsIcon() {
  return (
    <svg width={12} height={12} viewBox="0 0 12 12" fill="none" aria-hidden>
      <path
        d="M6.875 6C6.875 6.48325 6.48325 6.875 6 6.875C5.51675 6.875 5.125 6.48325 5.125 6C5.125 5.51675 5.51675 5.125 6 5.125C6.48325 5.125 6.875 5.51675 6.875 6Z"
        fill="currentColor"
      />
      <path
        d="M10.375 6C10.375 6.48325 9.98325 6.875 9.5 6.875C9.01675 6.875 8.625 6.48325 8.625 6C8.625 5.51675 9.01675 5.125 9.5 5.125C9.98325 5.125 10.375 5.51675 10.375 6Z"
        fill="currentColor"
      />
      <path
        d="M3.375 6C3.375 6.48325 2.98325 6.875 2.5 6.875C2.01675 6.875 1.625 6.48325 1.625 6C1.625 5.51675 2.01675 5.125 2.5 5.125C2.98325 5.125 3.375 5.51675 3.375 6Z"
        fill="currentColor"
      />
    </svg>
  );
}

function StopRedIcon() {
  return (
    <svg width={12} height={12} viewBox="0 0 12 12" fill="none" aria-hidden>
      <path
        d="M7 1C9.20914 1 11 2.79086 11 5V7C11 9.20914 9.20914 11 7 11H5C2.79086 11 1 9.20914 1 7V5C1 2.79086 2.79086 1 5 1H7Z"
        fill="#DC2626"
      />
    </svg>
  );
}

function SendIcon({ size = 16, className = "" }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden className={className}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.5889 3.577a.833.833 0 0 0-1.178 0L4.827 8.16a.833.833 0 1 0 1.178 1.179L9.167 6.178V15.833a.833.833 0 1 0 1.666 0V6.178l3.161 3.161a.833.833 0 1 0 1.178-1.178L10.59 3.577Z"
        fill="currentColor"
      />
    </svg>
  );
}

function formatAttachmentSize(bytes = 0) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileAttachmentChip({ file, onRemove }: { file: SelectedChatFile; onRemove?: () => void }) {
  const isImage = file.type?.startsWith("image/") && file.preview;
  const dot = file.name.lastIndexOf(".");
  const baseName = dot > 0 ? file.name.slice(0, dot) : file.name;
  const extension = dot > 0 ? file.name.slice(dot) : "";

  if (isImage) {
    return (
      <div className="flex self-stretch">
        <div className="overflow-hidden relative w-14 h-14 rounded-lg bg-neutral-100">
          <img src={file.preview!} alt={file.name} className="object-cover w-full h-full" />
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${file.name}`}
            className="absolute right-1 top-1 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-white text-[#0A0A0A] shadow-[0_1px_3px_rgba(0,0,0,0.1)]"
          >
            <CloseIcon className="h-2.5 w-2.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2 items-center px-2 py-1 bg-white rounded-lg border max-w-56 border-neutral-200">
      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg border border-[#F0F2F5] bg-[#FAFAFA] text-[#525252]">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M7 3.75h6.75L18.25 8.25V20.25H7V3.75Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path d="M13.5 4V8.5H18" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex min-w-0 text-xs font-medium text-[#101928]">
          <span className="truncate">{baseName}</span>
          <span className="flex-shrink-0">{extension}</span>
        </div>
        <p className="text-[10px] text-[#475367]">{formatAttachmentSize(file.size)}</p>
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${file.name}`}
        className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[#525252] hover:bg-neutral-100"
      >
        <CloseIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function mapVoiceChatPhase(status: VoiceSessionStatus): VoiceChatPhase | null {
  if (status === "connecting" || status === "requesting-mic" || status === "waiting-ready") {
    return "connecting";
  }
  if (status === "listening") return "listening";
  if (status === "thinking") return "thinking";
  if (status === "speaking") return "speaking";
  if (status === "stopping") return "stopping";
  return null;
}

function mapVoiceNotePhase(
  mode: VoiceInputMode,
  status: VoiceSessionStatus,
  userTranscript: string,
  voiceNoteRecorded: boolean,
  sessionError: string | null,
): VoiceNotePhase | null {
  if (mode !== "note" && !voiceNoteRecorded) return null;
  if (voiceNoteRecorded) return "recorded";
  if (sessionError && status === "idle") return "error";
  if (status === "connecting" || status === "requesting-mic" || status === "waiting-ready") {
    return "connecting";
  }
  if (status === "transcribing") return "transcribing";
  if (status === "listening" || status === "stopping") {
    return "listening";
  }
  return null;
}

export default function FloatingChatInput({
  input,
  setInput,
  onSend,
  onUploadClick,
  loading = false,
  inputRef,
  onKeyDown,
  handleKeyPress,
  selectedFile = null,
  onRemoveFile,
  multiline = false,
  showUpload = true,
  showDisclaimer = true,
  showVoice = true,
  className = "",
  placeholder = "Ask or search for anything",
  voiceStatus = "idle",
  voiceMode = null,
  userTranscript = "",
  assistantTranscript = "",
  voiceNoteRecorded = false,
  voiceNoteDraft = "",
  voiceSessionError = null,
  onClearVoiceSessionError,
  onVoiceNoteEdited,
  onStartVoiceChat,
  onStartVoiceNote,
  onStopVoiceChat,
  onCancelVoice,
  onConfirmVoiceNote,
  voiceMediaStream: _voiceMediaStream = null,
}: FloatingChatInputProps) {
  const [focused, setFocused] = useState(false);

  const voiceChatPhase = voiceMode === "chat" ? mapVoiceChatPhase(voiceStatus) : null;
  const voiceNotePhase = mapVoiceNotePhase(
    voiceMode,
    voiceStatus,
    userTranscript,
    voiceNoteRecorded,
    voiceSessionError,
  );

  const isVoiceNoteRecording =
    voiceNotePhase === "connecting" || voiceNotePhase === "listening" || voiceNotePhase === "error";
  const isVoiceNoteActive = isVoiceNoteRecording || voiceNotePhase === "transcribing" || voiceNotePhase === "recorded";
  const isVoiceChatActive = voiceChatPhase !== null;
  const hasText = Boolean(input?.trim() || voiceNoteDraft?.trim());
  const recordedText = voiceNoteRecorded
    ? input.trim() || voiceNoteDraft.trim() || userTranscript.trim()
    : userTranscript;

  const canSend =
    !loading &&
    !isVoiceNoteRecording &&
    !isVoiceChatActive &&
    Boolean(hasText || (voiceNotePhase === "recorded" && recordedText.trim()));

  const isActive = focused || hasText || Boolean(selectedFile) || isVoiceChatActive || isVoiceNoteActive;

  const voiceChatStatusLabel =
    voiceStatus === "requesting-mic"
      ? "Allow microphone access..."
      : voiceStatus === "waiting-ready"
        ? "Waiting for voice service..."
        : voiceChatPhase === "connecting"
          ? "Connecting..."
          : voiceChatPhase === "listening"
            ? "Listening..."
            : voiceChatPhase === "thinking"
              ? "Thinking..."
              : voiceChatPhase === "speaking"
                ? assistantTranscript.trim() || "Speaking..."
                : "Stopping...";

  const voiceNoteStatusLabel =
    voiceStatus === "requesting-mic"
      ? "Allow microphone access..."
      : voiceStatus === "waiting-ready"
        ? "Waiting for voice service..."
        : voiceNotePhase === "connecting"
          ? "Connecting..."
          : voiceNotePhase === "listening"
            ? "Listening..."
            : voiceNotePhase === "error"
              ? "Voice service unavailable"
              : voiceNotePhase === "transcribing"
                ? userTranscript || "Transcribing..."
                : "";

  const voiceShellClass =
    // The chat switches to its mobile layout at 768px, which is wider than the
    // `sm` breakpoint, so the compact sizes key off that same threshold.
    "min-h-[44px] rounded-[40px] border border-neutral-200 bg-white px-3 py-2 shadow-[0px_1px_3px_0_#bfdbfe,0px_1px_2px_-1px_#bfdbfe] max-[768px]:min-h-[38px] max-[768px]:py-1 sm:px-4";

  const voiceChatActiveShellClass =
    "min-h-[44px] rounded-[40px] border border-[#2563EB] bg-white px-3 py-2 shadow-[0_0_0_3px_rgba(37,99,235,0.12),0_1px_6px_rgba(16,24,40,0.06)] max-[768px]:min-h-[38px] max-[768px]:py-1 sm:px-4";

  const focusedInputShellClass =
    "rounded-full border border-[#2563EB] bg-white px-3 py-1.5 min-h-[44px] shadow-[0_0_0_3px_rgba(37,99,235,0.12),0_1px_6px_rgba(16,24,40,0.06)]";

  const idleInputShellClass =
    "rounded-full border border-[#D0D5DD] bg-white px-3 py-1.5 min-h-[44px] shadow-[0_1px_6px_rgba(16,24,40,0.06)] hover:border-[#98A2B3]";

  useEffect(() => {
    if (!voiceNoteRecorded) return;
    const text = voiceNoteDraft.trim() || userTranscript.trim();
    if (!text || input.trim() === text) return;
    setInput(text);
  }, [voiceNoteRecorded, voiceNoteDraft, userTranscript, input, setInput]);

  const handleSend = () => {
    if (!canSend && voiceNotePhase !== "recorded") return;
    onSend();
  };

  const handleKey = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !isVoiceChatActive && !isVoiceNoteRecording) {
      e.preventDefault();
      if (canSend) handleSend();
      return;
    }
    onKeyDown?.(e);
    handleKeyPress?.(e);
  };

  const fieldClass =
    "flex-1 min-w-0 bg-transparent border-0 outline-none resize-none text-[14px] leading-6 text-[#344054] placeholder-[#98A2B3] disabled:opacity-50 focus:ring-0";

  const renderSendButton = () => (
    <button
      type="button"
      onClick={handleSend}
      disabled={loading || (!canSend && voiceNotePhase !== "recorded")}
      className={cn(
        "flex-shrink-0 w-9 h-9 min-w-[36px] min-h-[36px] rounded-full flex items-center justify-center transition-all active:scale-[0.97]",
        loading || (!canSend && voiceNotePhase !== "recorded")
          ? "bg-[#B8D4F8] text-white cursor-not-allowed"
          : "bg-[#2563EB] hover:bg-[#1C3BC7] active:bg-[#1C3BC7] text-white cursor-pointer",
      )}
      aria-label="Send message"
    >
      {loading ? (
        <CircularProgress size={multiline ? 16 : 14} sx={{ color: "#fff" }} />
      ) : (
        <SendIcon size={multiline ? 16 : 14} />
      )}
    </button>
  );

  const renderVoiceIcons = () => (
    <div className="flex flex-shrink-0 items-center -space-x-1">
      <button
        type="button"
        onClick={() => onStartVoiceNote?.()}
        disabled={loading}
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[#667185] hover:bg-[#F0F2F5] disabled:opacity-50"
        aria-label="Record voice note"
      >
        <MicrophoneIcon className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={() => onStartVoiceChat?.()}
        disabled={loading}
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[#667185] hover:bg-[#F0F2F5] disabled:opacity-50"
        aria-label="Start voice chat"
      >
        <WaveformBarsIcon className="w-5 h-5" />
      </button>
    </div>
  );

  const renderTextField = () => {
    if (isVoiceChatActive) {
      return <p className="flex-1 min-w-0 text-sm truncate text-neutral-500">{voiceChatStatusLabel}</p>;
    }

    if (voiceNotePhase === "recorded" || voiceNotePhase === "transcribing") {
      const displayText =
        voiceNotePhase === "recorded" ? input.trim() || voiceNoteDraft.trim() || userTranscript.trim() : userTranscript;
      return (
        <textarea
          ref={inputRef as RefObject<HTMLTextAreaElement>}
          value={displayText}
          onChange={(e) => {
            if (voiceNotePhase === "recorded") {
              onVoiceNoteEdited?.();
              setInput(e.target.value);
            }
          }}
          readOnly={voiceNotePhase === "transcribing"}
          rows={3}
          className={cn(
            fieldClass,
            "w-full min-h-[72px] max-h-[160px] py-1 leading-6",
            voiceNotePhase === "transcribing" && "text-[#344054]",
          )}
        />
      );
    }

    if (isVoiceNoteRecording) {
      return (
        <div className="flex overflow-hidden flex-1 gap-2 items-center min-w-0">
          {voiceNotePhase === "error" ? (
            <button
              type="button"
              onClick={() => onClearVoiceSessionError?.()}
              className="text-xs font-medium text-red-600 underline"
            >
              Dismiss
            </button>
          ) : null}
          {voiceNotePhase === "connecting" || voiceStatus === "requesting-mic" ? (
            <LoadingSunIcon className="flex-shrink-0 w-4 h-4 text-blue-600" />
          ) : voiceNotePhase === "error" ? null : (
            <VoiceNoteActivityDots />
          )}
          <p className={cn("flex-shrink-0 text-sm", voiceNotePhase === "error" ? "text-red-600" : "text-neutral-500")}>
            {voiceNoteStatusLabel}
          </p>
          {voiceNotePhase === "listening" ? (
            <VoiceNoteWaveform active />
          ) : null}
        </div>
      );
    }

    if (multiline) {
      return (
        <textarea
          ref={inputRef as RefObject<HTMLTextAreaElement>}
          placeholder={placeholder}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            e.target.style.height = "auto";
            e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
          }}
          onKeyDown={handleKey}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={loading}
          rows={1}
          className={cn(fieldClass, "self-center py-1 min-h-[24px] max-h-[120px]")}
        />
      );
    }

    return (
      <input
        ref={inputRef as RefObject<HTMLInputElement>}
        type="text"
        placeholder={placeholder}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKey}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        disabled={loading}
        className={cn(fieldClass, "!h-auto !min-h-[24px] py-0")}
      />
    );
  };

  const renderTrailingActions = () => {
    if (isVoiceChatActive) {
      if (voiceChatPhase === "connecting" || voiceChatPhase === "stopping") {
        if (voiceChatPhase === "stopping") {
          return (
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-neutral-100 max-[768px]:h-5 max-[768px]:w-5">
              <LoadingSunIcon className="h-4 w-4 text-neutral-900 max-[768px]:h-3.5 max-[768px]:w-3.5" />
            </div>
          );
        }
        return (
          <button
            type="button"
            onClick={() => onCancelVoice?.()}
            className="flex h-7 flex-shrink-0 items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 text-xs font-medium leading-none text-neutral-900 max-[768px]:h-5"
            aria-label="Cancel voice chat"
          >
            <CancelDotsIcon />
            Cancel
          </button>
        );
      }

      return (
        <button
          type="button"
          onClick={() => onStopVoiceChat?.()}
          className="flex h-7 flex-shrink-0 items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-2.5 text-xs font-medium leading-none text-red-600 shadow-[0px_1px_2px_0_rgba(0,0,0,0.05)] max-[768px]:h-5"
          aria-label="Stop voice chat"
        >
          <StopRedIcon />
          Stop
        </button>
      );
    }

    if (isVoiceNoteRecording || voiceNotePhase === "transcribing") {
      if (voiceNotePhase === "transcribing") {
        return (
          <div className="flex flex-shrink-0 justify-center items-center w-9 h-9" aria-label="Transcribing voice note">
            <LoadingSunIcon className="w-4 h-4 text-blue-600" />
          </div>
        );
      }
      return (
        <div className="flex flex-shrink-0 gap-1 items-center">
          <button
            type="button"
            onClick={() => onCancelVoice?.()}
            className="flex h-9 w-9 items-center justify-center rounded-full text-[#667185] hover:bg-[#F0F2F5]"
            aria-label="Cancel voice note"
          >
            <CloseIcon />
          </button>
          {voiceNotePhase !== "error" ? (
            <button
              type="button"
              onClick={() => onConfirmVoiceNote?.()}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-[#2563EB] text-white"
              aria-label="Confirm voice note"
            >
              <CheckIcon />
            </button>
          ) : null}
        </div>
      );
    }

    if (canSend || hasText || voiceNotePhase === "recorded") {
      return renderSendButton();
    }

    if (showVoice) return renderVoiceIcons();
    return renderSendButton();
  };

  const isVoiceNoteExpanded = voiceNotePhase === "transcribing" || voiceNotePhase === "recorded";

  const renderVoiceLeadingIcon = () => {
    if (!isVoiceChatActive && !isVoiceNoteRecording) return null;

    return (
      <div className="flex flex-shrink-0 items-center p-0.5 opacity-50">
        <div className="flex justify-center items-center p-1 w-6 h-6 rounded-full">
          <PlusIcon className="text-neutral-900" />
        </div>
      </div>
    );
  };

  const renderVoiceNoteComposer = () => {
    if (!voiceNotePhase) return null;

    if (voiceNotePhase === "recorded") {
      const value = input || voiceNoteDraft || userTranscript;
      return (
        <div className="flex w-full flex-col rounded-3xl border border-[#2563EB] bg-white px-4 py-3 shadow-[0_1px_3px_#bfdbfe,0_1px_2px_-1px_#bfdbfe]">
          <textarea
            ref={inputRef as RefObject<HTMLTextAreaElement>}
            value={value}
            onChange={(event) => {
              onVoiceNoteEdited?.();
              setInput(event.target.value);
            }}
            onKeyDown={handleKey}
            rows={2}
            autoFocus
            className="min-h-[48px] w-full resize-none border-0 bg-transparent text-[14px] leading-6 text-[#344054] outline-none focus:ring-0"
          />
          <div className="flex justify-between items-center mt-2">
            <button
              type="button"
              onClick={() => onCancelVoice?.()}
              className="flex h-8 items-center gap-1.5 rounded-full px-2 text-[13px] font-medium text-[#667185] hover:bg-[#F0F2F5]"
              aria-label="Discard transcribed voice note"
            >
              <CloseIcon className="h-3.5 w-3.5" />
              Discard
            </button>
            {renderSendButton()}
          </div>
        </div>
      );
    }

    const transcribing = voiceNotePhase === "transcribing";
    const connecting = voiceNotePhase === "connecting";
    const error = voiceNotePhase === "error";

    return (
      <div
        className={cn(
          "flex w-full flex-col rounded-3xl border bg-white px-4 py-3 shadow-[0_1px_3px_#bfdbfe,0_1px_2px_-1px_#bfdbfe]",
          transcribing ? "border-[#2563EB]" : "border-neutral-200",
        )}
      >
        <div
          className={cn(
            "mb-2 truncate px-1 text-[14px] leading-5",
            transcribing ? "italic text-[#667185]" : error ? "text-red-600" : "text-[#667185]",
          )}
        >
          {transcribing
            ? userTranscript || "Transcribing your voice note…"
            : error
              ? voiceSessionError || "Voice service unavailable"
              : connecting
                ? "Connecting..."
                : "Listening..."}
        </div>

        <div className="flex h-10 items-center gap-2 rounded-full bg-[#F7F9FC] px-1.5">
          <button
            type="button"
            onClick={() => (error ? onClearVoiceSessionError?.() : onCancelVoice?.())}
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white text-[#98A2B3] shadow-[0_1px_2px_rgba(0,0,0,0.08)]"
            aria-label={error ? "Dismiss voice-note error" : "Cancel voice note"}
          >
            <CloseIcon className="h-3.5 w-3.5" />
          </button>

          <div className="flex min-w-0 flex-1 items-center overflow-hidden">
            {connecting ? (
              <div className="h-[3px] flex-1 rounded-full bg-[#E4E7EC]" />
            ) : transcribing ? (
              <VoiceNoteWaveform animated />
            ) : error ? null : (
              <VoiceNoteWaveform active />
            )}
          </div>

          {transcribing ? (
            <div className="flex flex-shrink-0 justify-center items-center w-7 h-7">
              <LoadingSunIcon className="h-4 w-4 text-[#2563EB]" />
            </div>
          ) : (
            <button
              type="button"
              disabled={connecting || error}
              onClick={() => onConfirmVoiceNote?.()}
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#2563EB] text-white disabled:bg-[#8FB4F4]"
              aria-label="Finish voice note"
            >
              <CheckIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={cn("w-full", className)}>
      {voiceNotePhase ? (
        <>
          {renderVoiceNoteComposer()}
          {showDisclaimer ? (
            <div className="mt-1.5 pt-1">
              <p className="overflow-hidden text-ellipsis whitespace-nowrap px-1 text-center text-[10px] leading-tight text-[#98A2B3] sm:text-[11px]">
                {DISCLAIMER}
              </p>
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className="flex relative flex-col items-center">
            <div
              className={cn(
                "relative z-10 w-full",
                isVoiceNoteExpanded
                  ? "flex flex-col gap-2 rounded-2xl border border-[#2563EB] bg-white px-3 py-3 shadow-[0px_1px_3px_0_#bfdbfe,0px_1px_2px_-1px_#bfdbfe]"
                  : selectedFile
                    ? "flex flex-col gap-4 rounded-3xl border border-neutral-200 bg-white px-4 py-3 shadow-[0_1px_3px_#bfdbfe,0_1px_2px_-1px_#bfdbfe]"
                    : isVoiceChatActive
                      ? cn("flex items-center gap-2", voiceChatActiveShellClass)
                      : isVoiceNoteRecording
                        ? cn("flex items-center gap-2", voiceShellClass)
                        : cn(
                            "flex items-center gap-2 transition-all",
                            isActive ? focusedInputShellClass : idleInputShellClass,
                          ),
              )}
            >
              {selectedFile && !isVoiceChatActive ? (
                <>
                  <FileAttachmentChip file={selectedFile} onRemove={onRemoveFile} />
                  <div className="flex gap-2 items-center w-full">
                    {showUpload && onUploadClick ? (
                      <button
                        type="button"
                        onClick={onUploadClick}
                        disabled={loading}
                        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[#0A0A0A] hover:bg-[#F0F2F5]"
                        aria-label="Attach another file"
                      >
                        <PlusIcon />
                      </button>
                    ) : null}
                    {renderTextField()}
                    {renderTrailingActions()}
                  </div>
                </>
              ) : isVoiceNoteExpanded ? (
                <>
                  {renderTextField()}
                  <div className="flex gap-2 justify-between items-center">
                    <div className="flex gap-2 items-center">
                      {showUpload && onUploadClick && !voiceNoteRecorded ? (
                        <button
                          type="button"
                          onClick={onUploadClick}
                          disabled={loading}
                          className="flex h-7 w-7 items-center justify-center rounded-full text-[#667185] hover:bg-[#F0F2F5]"
                          aria-label="Attach file"
                        >
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </button>
                      ) : null}
                      {voiceNotePhase === "transcribing" ? (
                        <VoiceNoteWaveform animated />
                      ) : null}
                    </div>
                    {renderTrailingActions()}
                  </div>
                </>
              ) : (
                <>
                  {renderVoiceLeadingIcon()}

                  {showUpload && onUploadClick && !isVoiceChatActive && !isVoiceNoteActive ? (
                    <button
                      type="button"
                      onClick={onUploadClick}
                      disabled={loading}
                      className="flex-shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-full text-[#667185] hover:bg-[#F0F2F5] active:bg-[#E4E7EC] transition-colors disabled:opacity-50"
                      aria-label="Attach file"
                    >
                      {multiline ? (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      ) : (
                        <AddIcon sx={{ fontSize: 20 }} />
                      )}
                    </button>
                  ) : null}

                  {renderTextField()}
                  {renderTrailingActions()}
                </>
              )}
            </div>
          </div>

          {showDisclaimer ? (
            <div className="mt-1.5 pt-1">
              <p className="text-center text-[10px] sm:text-[11px] leading-tight text-[#98A2B3] px-1 whitespace-nowrap overflow-hidden text-ellipsis">
                {DISCLAIMER}
              </p>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
